import { KdsSpinner } from "@kazaar-platform/design-system";
import { type ComponentType, createElement, lazy, Suspense } from "react";

// ============================================================================
// Page Fallback Component
// ============================================================================
export const PageFallback = () =>
  createElement(
    "div",
    { className: "flex h-full items-center justify-center" },
    createElement(KdsSpinner, { size: "lg" })
  );

// ============================================================================
// Lazy Page Helper - Combines lazy() + Suspense automatically
// ============================================================================
/**
 * Creates a lazy-loaded page component with Suspense wrapper built-in.
 * Usage: const MyPage = lazyPage(() => import("./MyPage"), (m) => m.MyPage);
 */
export const lazyPage = <M, C extends ComponentType>(
  importFn: () => Promise<M>,
  resolver: (module: M) => C
) => {
  const LazyComponent = lazy(() =>
    importFn().then((m) => ({
      default: resolver(m) as ComponentType<object>,
    }))
  );

  return () =>
    createElement(
      Suspense,
      { fallback: createElement(PageFallback) },
      createElement(LazyComponent)
    );
};

// ============================================================================
// Lazy loaded pages - Client
// ============================================================================
export const AccountPage = lazyPage(
  () => import("@/pages/AccountPage"),
  (m) => m.AccountPage
);
export const BriefAssistantPage = lazyPage(
  () => import("@/pages/BriefBuilderAssistantPage/BriefAssistantPage.tsx"),
  (m) => m.BriefAssistantPage
);
export const CatalogMediumPage = lazyPage(
  () => import("@/pages/CatalogMediumPage"),
  (m) => m.CatalogMediumPage
);
export const CatalogPage = lazyPage(
  () => import("@/pages/CatalogPage"),
  (m) => m.CatalogPage
);
export const InboxPage = lazyPage(
  () => import("@/pages/InboxPage/InboxPage.tsx"),
  (m) => m.InboxPage
);
export const OrderPage = lazyPage(
  () => import("@/pages/OrderPage"),
  (m) => m.OrderPage
);
export const OrdersPage = lazyPage(
  () => import("@/pages/OrdersPage"),
  (m) => m.OrdersPage
);
export const OrderSuccessPage = lazyPage(
  () => import("@/pages/OrderSuccessPage"),
  (m) => m.OrderSuccessPage
);
export const DashboardPage = lazyPage(
  () => import("@/pages/DashboardPage"),
  (m) => m.DashboardPage
);
export const ModificationRequestPage = lazyPage(
  () => import("@/pages/ModificationRequestPage"),
  (m) => m.ModificationRequestPage
);
export const OpenBriefPage = lazyPage(
  () => import("@/pages/OpenBriefPage"),
  (m) => m.OpenBriefPage
);
export const OrderItemPage = lazyPage(
  () => import("@/pages/OrderItemPage"),
  (m) => m.OrderItemPage
);
export const SentBriefPage = lazyPage(
  () => import("@/pages/CompletedBriefPage/SentBriefPage"),
  (m) => m.SentBriefPage
);

// ============================================================================
// Lazy loaded pages - Auth
// ============================================================================
export const ForgotPasswordPage = lazyPage(
  () => import("@/pages/Auth/ForgotPasswordPage/ForgotPasswordPage.tsx"),
  (m) => m.ForgotPasswordPage
);
export const ResetPasswordPage = lazyPage(
  () => import("@/pages/Auth/ResetPasswordPage/ResetPasswordPage.tsx"),
  (m) => m.ResetPasswordPage
);
export const SignInPage = lazyPage(
  () => import("@/pages/Auth/SignInPage/SignInPage.tsx"),
  (m) => m.SignInPage
);
export const SSOLoginPage = lazyPage(
  () => import("@/pages/Auth/SSOLoginPage/SSOLoginPage.tsx"),
  (m) => m.SSOLoginPage
);

// ============================================================================
// Lazy loaded pages - OMS
// ============================================================================
export const OmsInboxPage = lazyPage(
  () => import("@/oms/pages/InboxPage/InboxPage.tsx"),
  (m) => m.OmsInboxPage
);
export const OmsOrderTrackingDetailsAddRetroplanningFormPage = lazyPage(
  () => import("@/oms/pages/OrderTrackingDetailsAddRetroplanningFormPage"),
  (m) => m.OmsOrderTrackingDetailsAddRetroplanningFormPage
);
export const OmsOrganisationalEntitiesPage = lazyPage(
  () =>
    import(
      "@/oms/pages/OrganisationalEntitiesPage/OrganisationalEntitiesPage.tsx"
    ),
  (m) => m.OmsOrganisationalEntitiesPage
);
export const OmsEcoScoringFormPage = lazyPage(
  () => import("@/oms/pages/EcoScoringFormPage/EcoScoringFormPage.tsx"),
  (m) => m.OmsEcoScoringFormPage
);
export const OmsModificationRequestPage = lazyPage(
  () => import("@/oms/pages/ModificationRequestPage/"),
  (m) => m.ModificationRequestPage
);
export const OmsOrderDetailsAddQuoteFormPage = lazyPage(
  () => import("@/oms/pages/OrderDetailsAddQuoteFormPage"),
  (m) => m.OmsOrderDetailsAddQuoteFormPage
);
export const OmsOrderPage = lazyPage(
  () => import("@/oms/pages/OrderPage"),
  (m) => m.OrderPage
);
export const OmsOrdersPage = lazyPage(
  () => import("@/oms/pages/OrdersPage"),
  (m) => m.OmsOrdersPage
);
export const OmsSettingsFormPage = lazyPage(
  () => import("@/oms/pages/SettingsFormPage"),
  (m) => m.OmsSettingsFormPage
);
